import { skedInboxApi } from "./_skedInboxApi";
import { safeTagColor, serializeTagColor } from "@inbox/shared/tag-colors";

export type InboxTagAccessDto =
  | {
      scope: "organizational";
    }
  | {
      scope: "board";
      boardId: string;
    };

export type InboxTagDto = {
  id: string;
  label: string;
  color: string;
  access?: InboxTagAccessDto;
  archived?: boolean;
  archivedAt?: string;
  hidden?: boolean;
};

function normalizeTag<T extends { color?: string }>(tag: T): T {
  if (!tag?.color) {
    return tag;
  }

  return {
    ...tag,
    color: safeTagColor(tag.color),
  };
}

// Tag CRUD
export const getWorkspaceTags = async (): Promise<InboxTagDto[]> => {
  const { data } = await skedInboxApi.get("/tags");
  return (data?.items ?? []).map(normalizeTag);
};

export const createTag = async (tag: {
  label: string;
  color: string;
  access?: InboxTagAccessDto;
}) => {
  const { data } = await skedInboxApi.post("/tags", {
    ...tag,
    color: serializeTagColor(tag.color),
  });
  return normalizeTag(data);
};

export const updateTag = async (
  id: string,
  updates: {
    label?: string;
    color?: string;
    access?: InboxTagAccessDto;
    hidden?: boolean;
  }
) => {
  const { data } = await skedInboxApi.patch(`/tags/${id}`, {
    ...updates,
    ...(updates.color ? { color: serializeTagColor(updates.color) } : {}),
  });
  return normalizeTag(data);
};

export const deleteTag = async (id: string) => {
  const { data } = await skedInboxApi.delete(`/tags/${id}`);
  return data;
};

// Conversation-tag operations
export const addTagToConversation = async (
  conversationId: string,
  tagId: string
) => {
  const { data } = await skedInboxApi.put(
    `/conversations/${conversationId}/tags/${tagId}`
  );
  return data;
};

export const removeTagFromConversation = async (
  conversationId: string,
  tagId: string
) => {
  const { data } = await skedInboxApi.delete(
    `/conversations/${conversationId}/tags/${tagId}`
  );
  return data;
};

// Bulk operations
export const bulkAddTag = async (tagId: string, conversationIds: string[]) => {
  const { data } = await skedInboxApi.put(`/conversations/tags/${tagId}`, {
    ids: conversationIds,
  });
  return data;
};

export const bulkRemoveTag = async (
  tagId: string,
  conversationIds: string[]
) => {
  const { data } = await skedInboxApi.delete(`/conversations/tags/${tagId}`, {
    data: { ids: conversationIds },
  });
  return data;
};

export const getArchivedTags = async (): Promise<InboxTagDto[]> => {
  const { data } = await skedInboxApi.get<{ items: InboxTagDto[] }>(
    "/tags?archived=true"
  );
  return (data.items ?? []).map(normalizeTag);
};

export const restoreTag = async (id: string): Promise<InboxTagDto> => {
  const { data } = await skedInboxApi.patch<InboxTagDto>(`/tags/${id}/restore`);
  return normalizeTag(data);
};
